import React, {useReducer} from 'react';
import type {Moment} from 'moment';

import {Menu} from '@albert-io/atomic';
import type {QueryBuilder} from '@albert-io/json-api-framework/request/builder';
import {CleverNotLoggedInModal, CleverSyncModal} from 'client/Clever/CleverSync';
import {
  CleverSyncContext,
  CleverSyncDispatch,
  CleverSyncReducer
} from 'client/Clever/CleverSyncProvider';
import {getUserQuery} from 'client/Clever/util';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import {history} from 'client/history';
import useAreLatestQueriesReady from 'lib/hooks/useAreLatestQueriesReady';

// The clever user query is built by untyped legacy utils; these are the fields we read.
interface CleverUser {
  getCleverId: () => string | undefined;
  getDistrictCleverId: () => string | undefined;
  getMeta: () => {
    isAllowCleverSync: () => boolean;
    getLastCleverSyncDate: () => Moment | undefined;
    getCurrentCleverSyncId: () => string | undefined;
  };
}

interface Props {
  onNewClass: () => void;
  onNewFolder: () => void;
  trigger: React.ComponentProps<typeof Menu.Trigger>['children'];
}

const CleverMark = () => (
  <svg aria-hidden='true' className='h-[1em] w-[1em]' fill='none' viewBox='0 0 23 25'>
    <path
      clipRule='evenodd'
      d='M12.5349 24.3672C5.34217 24.3672 0 19.0156 0 12.251V12.1834C0 5.48577 5.23755 0 12.7445 0C17.3535 0 20.1118 1.48093 22.3812 3.63479L18.9595 7.43813C17.074 5.78901 15.1537 4.77934 12.7096 4.77934C8.58939 4.77934 5.62146 8.07757 5.62146 12.1161V12.1834C5.62146 16.2221 8.51953 19.5879 12.7096 19.5879C15.5029 19.5879 17.2137 18.511 19.1342 16.8283L22.5559 20.16C20.0419 22.7514 17.2487 24.3672 12.5349 24.3672Z'
      fill='currentColor'
      fillRule='evenodd'
    />
  </svg>
);

const CreateNewMenuItems = ({
  onImportFromClever,
  onNewClass,
  onNewFolder,
  trigger
}: Props & {onImportFromClever?: () => void}) => (
  <Menu closeOnItemClick position='bottom-start' positionFixed>
    <Menu.Trigger>{trigger}</Menu.Trigger>
    <Menu.Content>
      <Menu.Item readonly textProps={{color: 'secondary', size: 's'}}>
        Classrooms
      </Menu.Item>
      {onImportFromClever && (
        <Menu.Item onClick={onImportFromClever}>
          <span className='flex items-center'>
            <span className='o-menu__item-icon-container flex items-center justify-center self-center'>
              <CleverMark />
            </span>
            Import From Clever
          </span>
        </Menu.Item>
      )}
      <Menu.Item leftIconProps={{icon: 'chalkboard', iconStyle: 'regular'}} onClick={onNewClass}>
        New Class with Albert
      </Menu.Item>
      <Menu.Item className='mt-x2' readonly textProps={{color: 'secondary', size: 's'}}>
        Classwork
      </Menu.Item>
      <Menu.Item
        leftIconProps={{icon: 'file-lines', iconStyle: 'regular'}}
        onClick={() => history.pushState(null, '/assignment-builder/create-assignment')}
      >
        Assignment
      </Menu.Item>
      <Menu.Item leftIconProps={{icon: 'folder', iconStyle: 'regular'}} onClick={onNewFolder}>
        Folder
      </Menu.Item>
    </Menu.Content>
  </Menu>
);

// Same flow as the legacy classes page's Import & Sync button: open the sync
// modal for Clever-authed users, otherwise prompt to log back in with Clever.
const CreateNewMenuWithClever = ({user, ...menuProps}: Props & {user: CleverUser}) => {
  const [state, dispatch] = useReducer(CleverSyncReducer, {
    loading: false,
    lastSyncDate: user.getMeta().getLastCleverSyncDate(),
    currentlyInProgressSyncId: user.getMeta().getCurrentCleverSyncId(),
    userCleverDistrictId: user.getDistrictCleverId(),
    userCleverId: user.getCleverId()
  });

  const handleImportFromClever = () => {
    dispatch({
      type: state.userCleverId && state.userCleverDistrictId ? 'openModal' : 'openLogoutModal'
    });
  };

  return (
    <CleverSyncContext.Provider value={state}>
      <CleverSyncDispatch.Provider value={dispatch}>
        <CreateNewMenuItems onImportFromClever={handleImportFromClever} {...menuProps} />
        {state.isModalOpen && <CleverSyncModal />}
        {state.isLogoutModalOpen && <CleverNotLoggedInModal />}
      </CleverSyncDispatch.Provider>
    </CleverSyncContext.Provider>
  );
};

export const CreateNewMenu = (props: Props) => {
  // Clever gating mirrors the legacy classes page: user meta opt-in, never while masquerading.
  const {isReady} = useAreLatestQueriesReady([() => getUserQuery() as unknown as QueryBuilder]);
  const user = isReady ? (getUserQuery().getResource() as unknown as CleverUser) : null;
  const showCleverImport =
    Boolean(user?.getMeta().isAllowCleverSync()) && !masqueradeStore.isMasquerading();

  if (user && showCleverImport) {
    return <CreateNewMenuWithClever user={user} {...props} />;
  }
  return <CreateNewMenuItems {...props} />;
};
